import { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Button, Heading } from '@ets/design-system';
import { Navigate, useLocation } from 'react-router-dom';

import { useDispatch, useSelector } from '_common/hooks';

import { openModal } from '_common/modals/ModalsSlice';
import { resetLoginErrors } from 'Auth/redux/authSlice';
import { removeAccount } from 'Auth/redux/localStorageSlice';
import { useLoginSetupQuery } from '_common/services/api/authority';
import { useAuthContext } from 'Auth/AuthContext';

import SupportButton from '_common/components/SupportButton/SupportButton';
import AuthHero from 'Auth/components/AuthHero/AuthHero';

import SavedAccountsLogin from './SavedAccountsLogin/SavedAccountsLogin';
import ThirdPartyLogin from './ThirdPartyLogin/ThirdPartyLogin';
import FreshLogin from './FreshLogin/FreshLogin';

const SignInPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { accounts } = useAuthContext();
  const { state } = useLocation();
  const locationState = state as LocationState | null;
  const setupIntervalId = useRef(-1);

  const authenticated = useSelector((state) => state.auth.authenticated);
  const firstLogin = useSelector((state) => state.auth.firstLogin);
  const isUpdatePasswordForced = useSelector((state) => state.auth.isUpdatePasswordForced);
  const platform = useSelector((state) => state.app.platform);

  const [signInForm, setSignInForm] = useState<Auth.SignInState | undefined>();
  const [from, setFrom] = useState<{ pathname: string }>({ pathname: '/storage' });

  useEffect(() => {
    if (Object.keys(accounts).length !== 0) {
      setSignInForm('SAVED_ACCOUNTS');
    } else {
      setSignInForm('FRESH_LOGIN');
    }
  }, [accounts]);

  const { data: setupData, isError, refetch } = useLoginSetupQuery();

  useEffect(() => {
    if (isError) {
      setTimeout(() => {
        refetch();
      }, 2000);
    }
  }, [isError]);

  useEffect(() => {
    if (platform.browser.ie) {
      dispatch(openModal('BrowserSupportWarningModal'));
    }

    const from =
      locationState && locationState.from.pathname !== '/auth/updatepassword'
        ? locationState.from
        : { pathname: '/' };

    setFrom(from);
    if (from.pathname !== '/') {
      sessionStorage.setItem('redirect-sso', from.pathname);
    }
    return () => {
      /**
       * Resets unlock message
       */
      // dispatch(gotFeedback(null)); // Check if important (Messing up feedback given by unlocking account)
      clearInterval(setupIntervalId.current);
    };
  }, []);

  const changeSignInForm = (form: Auth.SignInState) => {
    dispatch(resetLoginErrors());
    setSignInForm(form);
  };

  const handleUseAnotherAccount = () => {
    changeSignInForm('FRESH_LOGIN');
  };

  const handleUseAccounts = () => {
    changeSignInForm('SAVED_ACCOUNTS');
  };

  const handleRemoveAccount = (id: UserId) => {
    dispatch(removeAccount(id));
    const ids = Object.keys(accounts);
    if (ids.length === 1 && ids[0] === id) {
      changeSignInForm('FRESH_LOGIN');
    }
  };

  const renderAuthRoute = () => {
    const hasSavedAccount = Object.keys(accounts).length > 0;
    switch (signInForm) {
      case 'SAVED_ACCOUNTS':
        return (
          <SavedAccountsLogin
            removeAccount={handleRemoveAccount}
            useAnotherAccount={handleUseAnotherAccount}
            setSignInForm={changeSignInForm}
          />
        );
      case 'FRESH_LOGIN':
        return (
          <div data-testid="sign-in-form-container" data-has-error={isError}>
            {setupData?.['3p'] && setupData['3p'].length > 0 && (
              <>
                <ThirdPartyLogin data={setupData['3p']} />
                {(setupData.allow_login_with_password || hasSavedAccount) && (
                  <Divider className="my-8">{intl.formatMessage({ id: 'global.or' })}</Divider>
                )}
              </>
            )}
            {setupData?.allow_login_with_password && <FreshLogin />}
            {hasSavedAccount && (
              <Button
                variant="standard"
                style={{ margin: '1rem 0 0 0' }}
                fullWidth
                size="l"
                onClick={handleUseAccounts}
                tabIndex={1}
                testId="sign-in-using-existing-account"
              >
                <FormattedMessage id="SIGN_IN_USING_EXISTING_ACCOUNT" />
              </Button>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (authenticated) {
    if (isUpdatePasswordForced) {
      return <Navigate to="/auth/updatepassword" />;
    }

    if (from?.pathname?.includes('//')) {
      return <Navigate to="/404" />;
    }

    return <Navigate to={from} />;
  }

  if (firstLogin) {
    return <Navigate to="/auth/welcome" />;
  }

  return (
    <div className="flex h-full min-h-150 w-full min-w-307.5">
      <AuthHero />
      <div className="flex h-full flex-1 flex-col items-center">
        <Heading size="l" className="text-disabled mt-48 mb-4">
          <FormattedMessage id="SIGN_IN_TO_DODOC" />
        </Heading>
        <div className="mt-12 w-180">{renderAuthRoute()}</div>
      </div>
      <SupportButton />
    </div>
  );
};

export default SignInPage;
